import React, { useState, useEffect } from 'react';
import { TableContainer, Table, TableBody, TableCell, TableHead, TableRow, TablePagination, IconButton, Box } from '@mui/material';
import { Grid, Paper, CircularProgress } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { ErrorAlert } from 'pages/error';
import i18next from 'i18next';
import { getMuiTablePaginationI18nProps } from './muiTablePaginationI18nProps';

interface PagedTableCell {
  span?: number | null;
  cell: React.ReactNode | null;
}

interface PagedTableProps {
  filter?: React.ReactNode | null;
  filterKey?: React.Key | null;
  headers: React.ReactNode[];
  rows?: (React.ReactNode | PagedTableCell | null)[][] | null;
  rowsLoading?: boolean;
  rowsErr?: any | null;
  refetchPage: (skip: number, take: number) => void;
  showRefetch?: boolean;
  defaultPageSize?: 5 | 10 | 20 | 50;
  hidden?: (boolean | null)[] | null;
  totalCount?: number; // optional known total for client-side paging
  onRowClick?: (index: number) => void;
  customHeaders?: React.ReactNode;
  tableProps?: {
    [key: string]: any;
  };
}

/**
 * Paginated table component with server-side or client-side paging support.
 * Handles filter changes, page navigation, and loading/error states.
 * Supports cell spanning via PagedTableCell objects.
 * Refetches data when filter, page, or page size changes.
 */
export default function PagedTable(props: PagedTableProps) {
  const [page, setPage] = useState<number>(0);
  const [take, setTake] = useState<number>(props.defaultPageSize || 10);
  const prevFilterKey = React.useRef<React.Key | null | undefined>(undefined);
  const prevTotalCount = React.useRef<number | undefined>(undefined);
  const prevPage = React.useRef<number | undefined>(undefined);
  const prevTake = React.useRef<number | undefined>(undefined);

  useEffect(() => {
    const filterChanged = prevFilterKey.current !== props.filterKey;
    const totalCountChanged = prevTotalCount.current !== props.totalCount;
    const pageChanged = prevPage.current !== page;
    const takeChanged = prevTake.current !== take;
    const isInitialRender = prevPage.current === undefined;

    // Refetch only when totalCount changes from 0 to N (IDs now available, like in offers.tsx)
    // Ignore changes from undefined to N (data arrived with same query, like in spaces.tsx)
    const shouldRefetchForTotalCount =
      totalCountChanged && prevTotalCount.current === 0 && props.totalCount !== undefined && props.totalCount > 0;

    // Update refs BEFORE any state changes
    prevFilterKey.current = props.filterKey;
    prevTotalCount.current = props.totalCount;
    prevPage.current = page;
    prevTake.current = take;

    if (filterChanged && !isInitialRender) {
      // Filter changed: reset to page 0 if needed, then fetch
      if (page !== 0) {
        setPage(0);
        // Don't fetch here - the page change will trigger another effect run
        return;
      }
      const pageSize = props.totalCount !== undefined ? take : take + 1;
      props.refetchPage(0, pageSize);
    } else if (pageChanged || takeChanged || isInitialRender || shouldRefetchForTotalCount) {
      const pageSize = props.totalCount !== undefined ? take : take + 1;
      props.refetchPage(page * take, pageSize);
    }
  }, [page, take, props.filterKey, props.totalCount]);
  const displayedRows: (React.ReactNode | PagedTableCell | null)[][] = React.useMemo(() => {
    if (props.rowsLoading) {
      return [[{ span: props.headers.length, cell: <CircularProgress size={24} /> }]] as any;
    }
    if (props.rowsErr) {
      return [[{ span: props.headers.length, cell: <ErrorAlert err={props.rowsErr} /> }]] as any;
    }
    const incoming = (props.rows || []) as (React.ReactNode | PagedTableCell | null)[][];
    return incoming.slice(0, take);
  }, [props.rowsLoading, props.rowsErr, props.rows, take, props.headers.length]);

  const fixedCount = React.useMemo(() => {
    if (props.totalCount !== undefined) return props.totalCount;
    if (!props.rows || props.rowsLoading || props.rowsErr) return page * take;
    const hasNext = props.rows.length > take;
    return hasNext ? (page + 2) * take : page * take + props.rows.length;
  }, [props.totalCount, props.rows, props.rowsLoading, props.rowsErr, page, take]);

  const _pager = () => (
    <>
      {!props.rowsLoading &&
        !props.rowsErr &&
        displayedRows &&
        displayedRows.length > 0 &&
        (props.totalCount === undefined || props.totalCount > 0) && (
          <TablePagination
            component="div"
            count={fixedCount}
            rowsPerPageOptions={[5, 10, 20, 50]}
            rowsPerPage={take}
            {...getMuiTablePaginationI18nProps()}
            page={page}
            onRowsPerPageChange={event => {
              setTake(parseInt(event.target.value));
              setPage(0);
            }}
            onPageChange={(event, p) => {
              setPage(p);
            }}
            showFirstButton
          />
        )}
      {props.showRefetch && (
        <IconButton onClick={() => props.refetchPage(page * take, take + 1)}>
          <RefreshIcon />
        </IconButton>
      )}
    </>
  );

  return (
    <Grid container>
      {props.filter && (
        <Grid item xs={12} sx={{ mb: 2 }}>
          {props.filter}
        </Grid>
      )}
      <Grid item xs={12}>
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
          <Table {...props.tableProps}>
            {props.customHeaders ? (
              props.customHeaders
            ) : (
              props.headers && props.headers.length > 0 && (
              <TableHead>
                <TableRow>
                  {props.headers
                    .filter((h, index) => (props.hidden ? !props.hidden[index] : true))
                    .map((h, i) => (
                      <TableCell key={i}>{h}</TableCell>
                    ))}
                </TableRow>
              </TableHead>
              )
            )}
            <TableBody key={`page-${page}-${take}-${props.filterKey}`}>
              {displayedRows.length > 0
                ? displayedRows.map((row, index) => (
                  <TableRow
                    key={index}
                    onClick={() => props.onRowClick?.(index)}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:nth-child(odd)': { backgroundColor: '#f9f9f9' }, cursor: props.onRowClick ? 'pointer' : 'default' }}
                  >
                    {row
                      .filter((r, index) => (props.hidden ? !props.hidden[index] : true))
                      .map((cell, index) => (
                        <TableCell key={index} {...(cell && (cell as any)['span'] ? { colSpan: (cell as any).span } : {})}>
                          {(cell && (cell as any).cell) || cell}
                        </TableCell>
                      ))}
                  </TableRow>
                ))
                : !props.rowsLoading && (
                  <TableRow>
                    <TableCell colSpan={props.headers.length} align="center">
                      {i18next.t('no-results')}
                    </TableCell>
                  </TableRow>
                )}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      <Grid item xs={12} sm={6}></Grid>
      <Grid item xs={12} sm={6}>
        <Box display="flex" justifyContent="flex-end">
          {_pager()}
        </Box>
      </Grid>
    </Grid>
  );
}
